import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import Sidebar from "../components/Sidebar";
import PhonePreview from "../components/PhonePreview";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, 
  TrendingUp, 
  AlertTriangle,
  Plus,
  Search,
  Flame,
  ChevronRight,
  Smartphone,
  MessageSquare,
  Star,
  Calendar,
  Clock,
  CheckCircle,
  X,
  Sparkles,
  ExternalLink
} from "lucide-react";

// Simulated data for demo when no real clients
const simulatedClients = [
  { client_id: "sim_1", name: "Ana García", phone: "+51987654321", goal: "Perder 10 kg en 3 meses", current_streak: 21, best_streak: 28, total_checkins: 45, status: "green", last_checkin: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100" },
  { client_id: "sim_2", name: "Carlos Mendoza", phone: "+51987654322", goal: "Ganar masa muscular", current_streak: 14, best_streak: 14, total_checkins: 32, status: "green", last_checkin: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100" },
  { client_id: "sim_3", name: "María López", phone: "+51987654323", goal: "Alimentación balanceada", current_streak: 7, best_streak: 21, total_checkins: 28, status: "green", last_checkin: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100" },
  { client_id: "sim_4", name: "Pedro Sánchez", phone: "+51987654324", goal: "Control de diabetes tipo 2", current_streak: 30, best_streak: 30, total_checkins: 56, status: "green", last_checkin: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100" },
  { client_id: "sim_5", name: "Lucía Torres", phone: "+51987654325", goal: "Reducir colesterol", current_streak: 3, best_streak: 15, total_checkins: 22, status: "yellow", last_checkin: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100" },
  { client_id: "sim_6", name: "Roberto Díaz", phone: "+51987654326", goal: "Mejorar energía diaria", current_streak: 0, best_streak: 12, total_checkins: 18, status: "red", last_checkin: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100" },
  { client_id: "sim_7", name: "Elena Vargas", phone: "+51987654327", goal: "Nutrición post-embarazo", current_streak: 12, best_streak: 18, total_checkins: 35, status: "green", last_checkin: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100" },
  { client_id: "sim_8", name: "Fernando López", phone: "+51987654328", goal: "Preparación para maratón", current_streak: 45, best_streak: 45, total_checkins: 68, status: "green", last_checkin: new Date(Date.now() - 30 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100" },
  { client_id: "sim_9", name: "Isabel Mora", phone: "+51987654329", goal: "Dieta vegetariana equilibrada", current_streak: 5, best_streak: 10, total_checkins: 15, status: "yellow", last_checkin: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=100" },
  { client_id: "sim_10", name: "Alejandro Cruz", phone: "+51987654330", goal: "Bajar grasa abdominal", current_streak: 9, best_streak: 14, total_checkins: 27, status: "green", last_checkin: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100" },
  { client_id: "sim_11", name: "Carmen Ruiz", phone: "+51987654331", goal: "Alimentación para hipotiroidismo", current_streak: 18, best_streak: 25, total_checkins: 42, status: "green", last_checkin: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100" },
  { client_id: "sim_12", name: "Miguel Ángel", phone: "+51987654332", goal: "Aumentar peso saludablemente", current_streak: 0, best_streak: 8, total_checkins: 12, status: "red", last_checkin: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), avatar: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=100" },
];

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showPhonePreview, setShowPhonePreview] = useState(false);
  const [selectedClientForPreview, setSelectedClientForPreview] = useState(null);
  const [newClient, setNewClient] = useState({
    name: "",
    phone: "",
    email: "",
    goal: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [clientsRes, statsRes] = await Promise.all([
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/nutritionist/stats`, { withCredentials: true })
      ]);
      
      // Use simulated data if no real clients
      const realClients = clientsRes.data;
      if (realClients.length === 0) {
        setClients(simulatedClients);
        setStats({
          total_clients: 12,
          active_clients: 10,
          at_risk_clients: 2,
          avg_adherence: 87
        });
      } else {
        setClients(realClients);
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Fallback to simulated data
      setClients(simulatedClients);
      setStats({
        total_clients: 12,
        active_clients: 10,
        at_risk_clients: 2,
        avg_adherence: 87
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/clients`, newClient, { withCredentials: true });
      toast.success("¡Cliente agregado exitosamente!");
      setIsAddModalOpen(false);
      setNewClient({ name: "", phone: "", email: "", goal: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Error al agregar cliente");
    }
  };

  const handlePreviewClient = (client) => {
    setSelectedClientForPreview(client);
    setShowPhonePreview(true);
  };

  const filteredClients = clients.filter(client => {
    const matchesFilter = filter === "all" || client.status === filter;
    const matchesSearch = client.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const styles = {
      green: "bg-[#178A6B]/10 text-[#178A6B] border-[#178A6B]/20",
      yellow: "bg-amber-500/10 text-amber-700 border-amber-200",
      red: "bg-red-500/10 text-red-700 border-red-200"
    };
    const labels = {
      green: "Al día",
      yellow: "1-2 días",
      red: "3+ días"
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const formatLastCheckin = (dateStr) => {
    if (!dateStr) return "Sin check-in";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} horas`;
    if (diffDays === 1) return "Hace 1 día";
    return `Hace ${diffDays} días`;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#FAFAFA]">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
          <div className="max-w-7xl mx-auto space-y-6">
            <div className="h-8 w-64 skeleton rounded-lg"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="h-32 skeleton rounded-xl"></div>
              ))}
            </div>
            <div className="h-96 skeleton rounded-xl"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 ml-0 lg:ml-64">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#0F4D40]">
                ¡Hola, {user?.name?.split(' ')[0] || 'Nutricionista'}! 👋
              </h1>
              <p className="text-gray-500 mt-1">
                Hoy tienes <span className="font-semibold text-[#178A6B]">{stats?.active_clients || 0} clientes activos</span>
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="rounded-xl bg-[#178A6B] hover:bg-[#0F4D40] text-white shadow-lg shadow-[#178A6B]/25 transition-all hover:shadow-xl hover:-translate-y-0.5"
                    data-testid="add-client-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Cliente
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md bg-white">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-[#0F4D40]">Agregar Nuevo Cliente</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddClient} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-700">Nombre completo</Label>
                      <Input
                        id="name"
                        value={newClient.name}
                        onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                        placeholder="Ana García"
                        required
                        className="rounded-xl border-gray-200 focus:border-[#178A6B] focus:ring-[#178A6B]/20"
                        data-testid="client-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700">Teléfono (WhatsApp)</Label>
                      <Input
                        id="phone"
                        value={newClient.phone}
                        onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                        placeholder="+51 987 654 321"
                        required
                        className="rounded-xl border-gray-200 focus:border-[#178A6B] focus:ring-[#178A6B]/20"
                        data-testid="client-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-gray-700">Email (opcional)</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                        placeholder="ana@email.com"
                        className="rounded-xl border-gray-200 focus:border-[#178A6B] focus:ring-[#178A6B]/20"
                        data-testid="client-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="goal" className="text-gray-700">Objetivo</Label>
                      <Input
                        id="goal"
                        value={newClient.goal}
                        onChange={(e) => setNewClient({...newClient, goal: e.target.value})}
                        placeholder="Perder 10 kg en 3 meses"
                        required
                        className="rounded-xl border-gray-200 focus:border-[#178A6B] focus:ring-[#178A6B]/20"
                        data-testid="client-goal-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="notes" className="text-gray-700">Notas (opcional)</Label>
                      <Textarea
                        id="notes"
                        value={newClient.notes}
                        onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                        placeholder="Información adicional..."
                        className="rounded-xl border-gray-200 focus:border-[#178A6B] focus:ring-[#178A6B]/20"
                        data-testid="client-notes-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full rounded-xl bg-[#178A6B] hover:bg-[#0F4D40] py-6 text-base font-semibold"
                      data-testid="submit-client-btn"
                    >
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Agregar y Activar Seguimiento
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </motion.div>

          {/* Stats Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6"
          >
            <div className="bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 sm:p-6 rounded-2xl group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Clientes Activos</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#0F4D40]">{stats?.active_clients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#0F4D40]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Users className="w-6 h-6 text-[#0F4D40]" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-xs text-[#178A6B]">
                <TrendingUp className="w-3 h-3 mr-1" />
                <span>+3 este mes</span>
              </div>
            </div>
            
            <div className="bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 sm:p-6 rounded-2xl group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Adherencia (7d)</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#178A6B]">{stats?.avg_adherence || 0}%</p>
                </div>
                <div className="w-12 h-12 bg-[#178A6B]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-6 h-6 text-[#178A6B]" />
                </div>
              </div>
              <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#178A6B] to-[#0F4D40] rounded-full transition-all"
                  style={{ width: `${stats?.avg_adherence || 0}%` }}
                ></div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 sm:p-6 rounded-2xl group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">En Riesgo</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#DC2626]">{stats?.at_risk_clients || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <AlertTriangle className="w-6 h-6 text-[#DC2626]" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-xs text-red-600">
                <span>Requieren atención</span>
              </div>
            </div>

            <div className="bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 sm:p-6 rounded-2xl group">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Check-ins Hoy</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#E57A4F]">
                    {clients.filter(c => {
                      if (!c.last_checkin) return false;
                      const today = new Date().toDateString();
                      return new Date(c.last_checkin).toDateString() === today;
                    }).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#E57A4F]/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CheckCircle className="w-6 h-6 text-[#E57A4F]" />
                </div>
              </div>
              <div className="mt-3 flex items-center text-xs text-[#E57A4F]">
                <Flame className="w-3 h-3 mr-1" />
                <span>¡Gran día!</span>
              </div>
            </div>
          </motion.div>

          {/* Client List */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-gray-100 shadow-sm rounded-2xl overflow-hidden"
          >
            {/* Filters */}
            <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-xl border-gray-200"
                  data-testid="search-clients-input"
                />
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {[
                  { key: "all", label: "Todos", count: clients.length },
                  { key: "green", label: "Al día", count: clients.filter(c => c.status === 'green').length },
                  { key: "yellow", label: "Alerta", count: clients.filter(c => c.status === 'yellow').length },
                  { key: "red", label: "Riesgo", count: clients.filter(c => c.status === 'red').length },
                ].map((f) => (
                  <Button
                    key={f.key}
                    variant={filter === f.key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setFilter(f.key)}
                    className={`rounded-xl whitespace-nowrap ${
                      filter === f.key 
                        ? "bg-[#0F4D40] hover:bg-[#0F4D40]/90" 
                        : "hover:bg-gray-50"
                    }`}
                    data-testid={`filter-${f.key}-btn`}
                  >
                    {f.label}
                    <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                      filter === f.key ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                      {f.count}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Racha</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Último check-in</th>
                    <th className="text-left p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="text-right p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {filteredClients.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="p-12 text-center">
                          <div className="flex flex-col items-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <Users className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 font-medium">
                              {clients.length === 0 
                                ? "No tienes clientes aún"
                                : "No se encontraron clientes"
                              }
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                              {clients.length === 0 
                                ? "¡Agrega tu primer cliente para empezar!"
                                : "Intenta con otro filtro o búsqueda"
                              }
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredClients.map((client, index) => (
                        <motion.tr 
                          key={client.client_id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="border-b border-gray-50 hover:bg-[#178A6B]/5 cursor-pointer transition-colors group"
                          onClick={() => {
                            if (!client.client_id.startsWith('sim_')) {
                              navigate(`/clients/${client.client_id}`);
                            }
                          }}
                          data-testid={`client-row-${client.client_id}`}
                        >
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="relative">
                                {client.avatar ? (
                                  <img 
                                    src={client.avatar} 
                                    alt={client.name}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-[#0F4D40]/10 rounded-full flex items-center justify-center">
                                    <span className="text-[#0F4D40] font-medium">
                                      {client.name.charAt(0)}
                                    </span>
                                  </div>
                                )}
                                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                                  client.status === 'green' ? 'bg-[#178A6B]' : 
                                  client.status === 'yellow' ? 'bg-[#F59E0B]' : 'bg-[#DC2626]'
                                }`}></div>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 group-hover:text-[#0F4D40] transition-colors">{client.name}</p>
                                <p className="text-sm text-gray-500 truncate max-w-[200px]">{client.goal}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 hidden sm:table-cell">
                            <div className="flex items-center gap-1.5">
                              <Flame className={`w-4 h-4 ${client.current_streak > 0 ? 'text-[#E57A4F]' : 'text-gray-300'}`} />
                              <span className="font-semibold text-gray-700">{client.current_streak}</span>
                              <span className="text-gray-400 text-sm">días</span>
                            </div>
                          </td>
                          <td className="p-4 hidden md:table-cell">
                            <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                              <Clock className="w-3.5 h-3.5" />
                              {formatLastCheckin(client.last_checkin)}
                            </div>
                          </td>
                          <td className="p-4">
                            {getStatusBadge(client.status)}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-[#178A6B] hover:text-[#0F4D40] hover:bg-[#178A6B]/10 rounded-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreviewClient(client);
                                }}
                                data-testid={`preview-client-${client.client_id}`}
                              >
                                <Smartphone className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-[#178A6B] hover:text-[#0F4D40] hover:bg-[#178A6B]/10 rounded-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!client.client_id.startsWith('sim_')) {
                                    navigate(`/clients/${client.client_id}`);
                                  }
                                }}
                                data-testid={`view-client-${client.client_id}`}
                              >
                                Ver <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Footer */}
            {filteredClients.length > 0 && (
              <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between text-sm text-gray-500">
                <span>Mostrando {filteredClients.length} de {clients.length} clientes</span>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#178A6B]" />
                  <span>Datos actualizados en tiempo real</span>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Phone Preview Modal */}
      <PhonePreview 
        isOpen={showPhonePreview}
        onClose={() => setShowPhonePreview(false)}
        client={selectedClientForPreview}
      />
    </div>
  );
};

export default Dashboard;
